/* eslint-disable */
import React, {
	useState,
	useMemo,
	useCallback,
	useEffect,
	useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { get } from 'lodash';

import { STORAGE_DEALS_SEARCH_PATH } from 'config/search';

// components
import { BackToTop } from 'components/core/BackToTop';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Page, Row, Column } from 'components/core/Page';
import { Spinner } from 'components/core/Spinner';
import { PaginationSummary } from 'components/core/Pagination';
import { SearchPagination } from 'components/search/SearchPagination';
import SEOContentVariation from 'components/search/SEOContentVariation/SEOContentVariation';
import StickySearchBar from 'components/organisms/StickySearchBar/StickySearchBar';
import EmbeddedSearchMap from 'components/search/EmbeddedSearchMap/EmbeddedSearchMap';
import FacilityList from 'components/organisms/FacilityList';
import { SearchCTCFooter } from 'components/search/SearchCTCFooter/SearchCTCFooter';
import SortDropDownVariation from 'components/search/SortDropdownVariation/SortDropDownVariation';
import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { trackDecision } from 'lib/optimizelyDebug';
import { DealsDropdown } from 'components/molecules';

// Filter components (selected at render time based on Optimizely flag)
import { SearchFilters } from 'components/organisms/SearchFilters';
import { SearchFilterContent } from 'components/search/SearchFilterContent';
import { VehicleSearchFilters } from 'components/search/VehicleSearchFilters';
import { TruckFilterContent } from 'components/search/TruckFilterContent';

// decorators

import withViewportWidth from 'decorators/withViewportWidth';
import {
	withTruckPromos,
	withSearchPageHandlers,
	withAdditionalFacilitiesIndicator,
} from 'decorators/search';
import { withSearchHandler, useWebPageSchema } from 'decorators';

// Redux/Selectors/actions
import { searchSelectorVariation } from 'store/selectors/search';
import {
	fetchSearchResults,
	fetchStorageDealsResults,
	mapRedoSearchAction,
} from 'store/search/slice';
import { fetchBestReviews } from 'store/reviews/slice';
import { getFacilityHoursAndDirection } from 'store/facilities/slice';
import {
	getLocalPhoneNumberByZipCode,
	getLocalPhoneNumberByCityState,
	getTollfreePhoneNumber,
} from 'store/pageMeta/slice';

// Utils
import { buildFacilityUrl } from 'utils/url';
import { isFoundationLarge } from 'utils/breakpoints';
import { formatSizeText } from 'utils/format';
import { withinCallCenterHours as computeCallCenterHours } from 'utils/withinCallCenterHours';

// Tracking
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import './searchPageVariation.scss';

import {
	calculateFilterCount,
	nearMeAmenityKeyMap,
	nearMeVehicleKeyMap,
} from '../../utils/filterCount';

import { SearchFiltersTray } from 'components/organisms/SearchFiltersTray';
import { Modal, ModalBody } from 'components/core/Modal';
import { buildSearchLink, getNearMeHeadline } from 'lib/search';
import { parseAmenityFromPath, getFilterParamForAmenity, parseSizeFromPath, parseVehicleFromPath } from 'lib/search/parseAmenityFromPath';
import loadable from '@loadable/component';

const UnitSizeGuide = loadable(
	() =>
		import(
			/* webpackChunkName: "UnitSizeGuide" */ 'components/unit/UnitSizeGuide'
		),
);

const sizeBuckets = ['5x10', '10x10', '10x15', '10x20'];

const DEALS_DROPDOWN_OPTIONS = [
	{ value: 'all', display: 'All Deals' },
	{ value: 'fmf', display: 'First Month Free' },
	{ value: 'additional', display: 'Additional Deals' },
];

// Only build the map CSS URL when the build-time hash is available. In
// local/dev environments REACTFOOT_VERSION may be undefined and we should
// avoid adding a broken link tag to the document head.
const hash = process.env.REACTFOOT_VERSION || null;
const cssUrl = hash
	? `https://philes.sparefoot.com/assets/${hash}/Map.css`
	: null;

// Function to dynamically load the CSS (no-op when url is falsy)
// Was created to fix a race condition with SearchMap component
const loadMapCss = (url) => {
	if (!url) return;
	if (typeof window !== 'undefined' && typeof document !== 'undefined') {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = url;
		link.onload = () => {
			// keep lightweight logging for debugging without noisy output
			console.info('Map CSS loaded');
		};
		link.onerror = (err) => console.error('Failed to load map css', err);
		document.head.appendChild(link);
	}
};

function withSearchPageVariationState(Component) {
	function SearchPageVariationWithState(ownProps) {
		const dispatch = useDispatch();
		const selectorResult = useSelector((state) =>
			searchSelectorVariation(state, ownProps),
		);
		const actions = useMemo(
			() =>
				bindActionCreators(
					{
						fetchSearchResults,
						fetchStorageDealsResults,
						fetchBestReviews,
						getFacilityHoursAndDirection,
						getLocalPhoneNumberByZipCode,
						getLocalPhoneNumberByCityState,
						getTollfreePhoneNumber,
						mapRedoSearchAction,
					},
					dispatch,
				),
			[dispatch],
		);
		const [callCenterOpen, setCallCenterOpen] = useState(true);
		useEffect(() => {
			setCallCenterOpen(computeCallCenterHours());
		}, []);

		const props = {
			...ownProps,
			...selectorResult,
			actions,
			withinCallCenterHours: callCenterOpen,
		};

		const navigate = useNavigate();
		const [filtersShowing, setFiltersShowing] = useState(false);
		const [mapShowing, setMapShowing] = useState(false);
		const [showHoursForFacility, setShowHoursForFacility] = useState(null);
		const [unitSizeGuideShowing, setUnitSizeGuideShowing] = useState(false);
		const [preselectedFacilityId, setPreselectedFacilityId] =
			useState(null);
		const [mobileView, setMobileView] = useState('list');

		const mapCssLoadedRef = useRef(false);
		useEffect(() => {
			if (mapCssLoadedRef.current) return;
			if (isFoundationLarge(props.viewportWidth) || mobileView === 'map') {
				loadMapCss(cssUrl);
				mapCssLoadedRef.current = true;
			}
		}, [mobileView, props.viewportWidth]);
		const [isSpinnerVisible, setIsSpinnerVisible] = useState(
			Boolean(props.loading),
		);
		const prevLoadingRef = useRef(props.loading);

		const nearMeHeadline = getNearMeHeadline(props.match);
		const primaryHeadlineFromNearMe = nearMeHeadline
			? { primaryHeadline: nearMeHeadline }
			: {};

		const units = useMemo(() => {
			const lowestPrices = get(
				props.searchContent,
				'pricingStats.lowestPrices',
				{},
			);
			const numericKeys = Object.keys(lowestPrices)
				.filter((k) => /^\d+$/.test(k))
				.sort((a, b) => Number(a) - Number(b));
			const hasNumericKeys = numericKeys.length > 0;
			const buckets = hasNumericKeys ? numericKeys : sizeBuckets;

			return buckets.reduce((unit, size) => {
				const lowestPricedUnit = get(
					props.searchContent?.pricingStats,
					['lowestPrices', size],
				);
				const averagePrice = get(props.searchContent?.pricingStats, [
					'averagePrices',
					size,
					'price',
				]);

				if (get(lowestPricedUnit, 'price', null) && averagePrice) {
					const sizeText = hasNumericKeys ? `${size}'` : size;
					return [
						...unit,
						{
							...lowestPricedUnit,
							formattedSizeText: hasNumericKeys
								? sizeText
								: formatSizeText(size),
							sizeText,
							averagePrice,
							reserveUrl: buildFacilityUrl(
								lowestPricedUnit.facility,
								lowestPricedUnit.facilityName,
								lowestPricedUnit?.facilityCity ||
									props.searchContent?.location?.city,
								lowestPricedUnit?.facilityState ||
									props.searchContent?.location?.state,
							),
						},
					];
				}
				return unit;
			}, []);
		}, [props.searchContent]);

		// For city-amenity pages (e.g. /Detroit-MI-self-storage/24-hour-access.html),
		// SearchPageWrapper pre-injects the amenity filter param into queryParams.
		// We need to know which param was injected so we can exclude it from
		// clearableFilterCount (same logic as near-me URL-preselected filters).
		const amenityLandingFilterParam = useMemo(() => {
			if (props.pageType === 'storageNearMeLandingPage') return null;
			const amenitySlug = parseAmenityFromPath(props.location?.pathname);
			return amenitySlug ? getFilterParamForAmenity(amenitySlug) : null;
		}, [props.location?.pathname, props.pageType]);

		const filterCount = useMemo(() => {
			const nearMeParams =
				props.match && props.pageType === 'storageNearMeLandingPage'
					? {
							storageAmenity:
								props.match?.params?.keyword || null,
							vehicle: props.match?.params?.keyword || null,
						}
					: {};
			const base = calculateFilterCount(
				props.searchParams,
				props.searchType,
				nearMeParams,
				props.location,
			);
			// For city-amenity pages the pre-injected filter param is always 'true'
			// in searchParams but section 1 of calculateFilterCount is gated on
			// location.search — so it returns 0 on the initial (clean) URL.
			// Add 1 to reflect that the amenity IS active so the badge stays consistent.
			const amenityBonus =
				amenityLandingFilterParam &&
				props.searchParams?.[amenityLandingFilterParam] === 'true' &&
				!props.location?.search
					? 1
					: 0;

			// For size CLPs (e.g. /5x5-storage-units.html), sqft is pre-injected
			// by SearchPageWrapper from the path but calculateFilterCount step 7 is
			// gated on location.search, so it returns 0 on the initial clean URL.
			// The API call uses landingPageSize (not sqft), so searchParams.sqft is
			// undefined in Redux on initial load — path presence alone is sufficient.
			const sizeSlugFromPath =
				props.pageType !== 'storageNearMeLandingPage'
					? parseSizeFromPath(props.location?.pathname)
					: null;
			const sizeLandingBonus =
				sizeSlugFromPath && !props.location?.search ? 1 : 0;

			// For vehicle CLPs (e.g. /boat-storage.html), vehicleTypes is pre-injected
			// by SearchPageWrapper from the path but calculateFilterCount step 2 is
			// gated on location.search, so it returns 0 on the initial clean URL.
			// The API call uses vehicle (not vehicleTypes), so searchParams.vehicleTypes
			// is undefined in Redux on initial load — path presence alone is sufficient.
			const vehicleFromPath =
				props.pageType !== 'storageNearMeLandingPage'
					? parseVehicleFromPath(props.location?.pathname)
					: null;
			const vehicleLandingBonus =
				vehicleFromPath && !props.location?.search ? 1 : 0;

			return base + amenityBonus + sizeLandingBonus + vehicleLandingBonus;
		}, [
			// props.searchParams covers .sqft and .vehicleTypes for size/vehicle CLP bonuses.
			// props.location covers .pathname (path parsing) and .search (clean URL gate).
			// No extra deps needed for parseSizeFromPath/parseVehicleFromPath — pure functions.
			props.searchParams,
			props.searchType,
			props.match,
			props.pageType,
			props.location,
			amenityLandingFilterParam,
		]);

		const nearMeParamsForFilters = useMemo(() => {
			if (!props.match || props.pageType !== 'storageNearMeLandingPage') {
				return {};
			}
			const keyword = props.match?.params?.keyword || null;
			return {
				storageAmenity:
					keyword && nearMeAmenityKeyMap[keyword] ? keyword : null,
				vehicle:
					keyword && nearMeVehicleKeyMap[keyword] ? keyword : null,
			};
		}, [props.match, props.pageType]);

		const clearableFilterCount = useMemo(() => {
			// For city-amenity pages, strip the pre-injected amenity param before
			// counting so Clear All stays disabled when no user-added filters are present.
			const paramsForCount =
				amenityLandingFilterParam && props.searchParams?.[amenityLandingFilterParam] === 'true'
					? { ...props.searchParams, [amenityLandingFilterParam]: undefined }
					: props.searchParams;
			return calculateFilterCount(
				paramsForCount,
				props.searchType,
				nearMeParamsForFilters,
				props.location,
			);
		}, [props.searchParams, props.searchType, nearMeParamsForFilters, props.location, amenityLandingFilterParam]);

		const openFilters = useCallback(() => setFiltersShowing(true), []);
		const closeFilters = useCallback(() => setFiltersShowing(false), []);
		const toggleFilters = useCallback(
			() => setFiltersShowing((prev) => !prev),
			[],
		);
		const toggleMobileView = useCallback(() => {
			if (!mapCssLoadedRef.current) {
				loadMapCss(cssUrl);
				mapCssLoadedRef.current = true;
			}
			typeof window !== 'undefined' &&
				window.scrollTo({ top: 0, behavior: 'smooth' });
			setMobileView((prev) => (prev === 'list' ? 'map' : 'list'));
		}, []);
		const showSpinner = useCallback(() => setIsSpinnerVisible(true), []);
		const hideSpinner = useCallback(() => setIsSpinnerVisible(false), []);
		const handleHideUnitGuide = useCallback(
			() => setUnitSizeGuideShowing(false),
			[],
		);
		const handleShowUnitGuide = useCallback(
			() => setUnitSizeGuideShowing(true),
			[],
		);

		const handleToggleFilters = useCallback(() => {
			setFiltersShowing((prev) => !prev);
		}, []);

		const onSearchSuccess = useCallback(
			() =>
				props.actions.fetchBestReviews(
					props.facilities.map((facility) => facility.id),
				),
			[props.actions, props.facilities],
		);

		const handleSearchParamChange = useCallback(
			(newParams) => {
				setIsSpinnerVisible(true);
				const isNearMePage =
					props.pageType === 'storageNearMeLandingPage';
				const isSizeCLP = /^city_([0-9x]+|mini_storage)_landing_page$/.test(props.pageType);
				const isVehicleCLP = props.pageType === 'vehicleLandingPage';
				const isAmenityCLP = props.pageType === 'amenityLandingPage';
				let searchPath =
					props.match?.params?.keyword === 'storage-deals'
						? STORAGE_DEALS_SEARCH_PATH
						: (isNearMePage || isSizeCLP || isVehicleCLP || isAmenityCLP) && props.match?.url
							? props.match.url.replace(/\.html$/, '') + '.html'
							: null;
				let paramsToBuildLink =
					Object.keys(props.queryParams || {}).length === 0
						? {
								...newParams,
								location: props.searchParams.location,
								subPageType: props.searchParams.subPageType,
								page: 1,
							}
						: { ...props.searchParams, ...newParams, page: 1 };
				navigate(buildSearchLink({ ...paramsToBuildLink }, searchPath));
				return { queryParams: newParams };
			},
			[navigate, props.searchParams, props.match, props.pageType],
		);

		const handleScrollToFacility = useCallback(
			(facilityId) => {
				const showMapFacilityCard = !isFoundationLarge(props.viewportWidth);
				if (typeof document !== 'undefined' && !showMapFacilityCard) {
					const element = document.getElementById(
						`facility-${facilityId}`,
					);
					if (element) {
						const stickyBar = document.querySelector(
							'.sticky-search-bar-variation',
						);
						const offset = stickyBar
							? stickyBar.offsetHeight + 20
							: 100;
						const elementPosition =
							element.getBoundingClientRect().top;
						const offsetPosition =
							elementPosition + window.pageYOffset - offset;
						window.scrollTo({
							top: offsetPosition,
							behavior: 'smooth',
						});
					}
				}
			},
			[props.viewportWidth],
		);

		useEffect(() => {
			onSearchSuccess();
			if (
				props.queryParams?.subPageType === 'nearMePages' &&
				props.location?.pathname === '/search.html'
			) {
				props.actions
					.fetchSearchResults(props.queryParams)
					.then(() => onSearchSuccess());
			}
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

		useEffect(() => {
			if (prevLoadingRef.current && !props.loading) {
				setIsSpinnerVisible(false);
			}
			if (!prevLoadingRef.current && props.loading) {
				setIsSpinnerVisible(true);
			}
			prevLoadingRef.current = props.loading;
		}, [props.loading]);

		return (
			<Component
				{...props}
				{...primaryHeadlineFromNearMe}
				units={units}
				filtersShowing={filtersShowing}
				mapShowing={mapShowing}
				showHoursForFacility={showHoursForFacility}
				unitSizeGuideShowing={unitSizeGuideShowing}
				preselectedFacilityId={preselectedFacilityId}
				mobileView={mobileView}
				isSpinnerVisible={isSpinnerVisible}
				filterCount={filterCount}
				nearMeParamsForFilters={nearMeParamsForFilters}
				clearableFilterCount={clearableFilterCount}
				openFilters={openFilters}
				closeFilters={closeFilters}
				toggleFilters={toggleFilters}
				toggleMobileView={toggleMobileView}
				showSpinner={showSpinner}
				hideSpinner={hideSpinner}
				handleHideUnitGuide={handleHideUnitGuide}
				handleShowUnitGuide={handleShowUnitGuide}
				handleToggleFilters={handleToggleFilters}
				onSearchSuccess={onSearchSuccess}
				handleSearchParamChange={handleSearchParamChange}
				handleScrollToFacility={handleScrollToFacility}
			/>
		);
	}
	SearchPageVariationWithState.displayName = 'SearchPageVariationWithState';
	return SearchPageVariationWithState;
}

export const enhance = (Component) =>
	withViewportWidth(
		withSearchPageVariationState(
			withSearchPageHandlers(
				withTruckPromos(
					withSearchHandler(
						withAdditionalFacilitiesIndicator(
							withPageViewTracking(PAGE_TYPES.SEARCH)(Component),
						),
					),
				),
			),
		),
	);

export function SearchPageVariation(props) {
	const {
		actions,
		handleSearchParamChange, // To be used by sortBy in filter modal
		handleHideUnitGuide,
		// isTruckSerp, // used in SearchFilters/page layout
		// openFilters, // kept to be used by top sticky search bar/footer
		// closeFilters, // kept to be used by top sticky search bar/footer
		toggleFilters,
		filtersShowing, // kept to be used by top sticky search bar/footer
		// filterComponent,
		queryParams,
		urlQueryParams,
		// showHoursForFacility, // SearchResults/SearchRail
		unitSizeGuideShowing,
		// showVehicleLengthTool, // SearchResults
		searchParams,
		viewportWidth,
		searchLocation, // FacilityCard
		searchId, // SearchResults
		searchPath, // SearchResults
		totalFacilities, // SearchMap/SearchResults
		facilities, // FacilityList/FacilityCards etc
		filterCount,
		// locationLabel, // SearchResults
		searchTerm, // Used in FacilityList for tracking
		searchType, // FacilityCard
		page, // SearchResults component
		canonicalUrl, // SearchResults/SearchBreadcrumbs
		cityUrl, // Breadcrumbs/SearchFilterContent
		facilitiesPerPage, // Used in FacilityList
		localPhone, // Used in FacilityCard
		stateUrl, // Used in breadcrumbs
		searchContent, // Seo search content below the map/facility cards
		numPages, // used in OG Facility List/SearchPagination
		phone, // Used in SearchResults/FacilityCard
		handleSearch, // primary search method used in Sticky Search Bar Variation and SearchRail
		faqContent, // used in SearchContent/FAQ
		faqShowing, // used in SearchContent
		firstListingIsVipEligible, // Used in FacilityList to highlight VIP facility
		vipAccount, // Used to determine if the account/facility is a VIP
		vipFacilityId, // Used in FacilityList to highlight VIP facility
		vipFacilityImages, // Used in VIP facility card header
		preselectedFacilityId, // Used on google map
		fmfFacilitiesCount, // Used in OG Facility List
		promoFacilitiesCount, // Used in OG Facility List
		pageType,
		facilitySelfStorageSchemas,
		baseUrl, // Used in OG Facility List
		loading, // Used to determine if search is loading
		units, // FacilityCards
		mobileView,
		toggleMobileView,
		highlightedFacility, // corresponds with map/highlightFacility action will be used for syncing map/list hovers
		isSpinnerVisible,
		showSpinner,
		hideSpinner,
		withinCallCenterHours,
		handleToggleFilters,
		isTruckSerp,
		isVehicleSerp,
		facetCounts,
		handleShowUnitGuide,
		priceTransparency,
		handleScrollToFacility,
		isStorageDealsPage,
		primaryHeadline,
		prevUrl, // SEO prev pagination URL
		nextUrl, // SEO next pagination URL
		match,
		nearMeParamsForFilters = {},
		clearableFilterCount,
	} = props;
	const schemaProps = useWebPageSchema(props);
	const { clpPageSchema, nearMeWebpageSchema, nearMeWebSiteSchema } = schemaProps;
	const [filtersDecision] = useDecision(OPTIMIZELY_FLAGS.FILTERS_TEST.key);
	trackDecision(OPTIMIZELY_FLAGS.FILTERS_TEST.key, filtersDecision);
	const useNewFilters = filtersDecision?.enabled || false;
	let newNearMeParams = {};
	if (match && pageType === 'storageNearMeLandingPage') {
		const keyword = match?.params?.keyword || null;
		newNearMeParams = {
			storageAmenity:
				keyword && nearMeAmenityKeyMap[keyword] ? keyword : null,
			vehicle: keyword && nearMeVehicleKeyMap[keyword] ? keyword : null,
		};
	}

	// Select filter component based on Optimizely flag
	let filterComponent;
	if (isTruckSerp) {
		filterComponent = <TruckFilterContent />;
	} else if (useNewFilters) {
		filterComponent = (
			<SearchFilters
				facetCounts={facetCounts}
				onShowUnitGuide={handleShowUnitGuide}
				nearMeParams={newNearMeParams}
			/>
		);
	} else if (isVehicleSerp) {
		filterComponent = <VehicleSearchFilters facetCounts={facetCounts} />;
	} else {
		filterComponent = (
			<SearchFilterContent
				onShowUnitGuide={handleShowUnitGuide}
				priceTransparency={priceTransparency}
				cityUrl={cityUrl}
				pageType={pageType}
				facetCounts={facetCounts}
			/>
		);
	}

	const isSearchRoute =
		match && match.url && match.url.includes('/search.html');

	const isNearMePage = pageType === 'storageNearMeLandingPage';

	const dealsDropdownValue = queryParams?.dealsFilter || 'all';

	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);

	const showMap = mounted && (isFoundationLarge(viewportWidth) || mobileView === 'map');
	const showListColumn = mounted
		? isFoundationLarge(viewportWidth) || mobileView === 'list'
		: true;

	// Helper function to determine the pagination search path
	const getPaginationSearchPath = () => {
		if (isStorageDealsPage) {
			return STORAGE_DEALS_SEARCH_PATH;
		}
		if (pageType === 'storageNearMeLandingPage' && match?.url) {
			return match.url.replace(/\.html$/, '') + '.html';
		}
		return searchPath;
	};
	const hasNonPaginationParams =
		isNearMePage &&
		urlQueryParams &&
		Object.keys(urlQueryParams).some((p) => p !== 'page');
	const shouldNoIndex =
		!!searchParams.informedSearch ||
		isSearchRoute ||
		hasNonPaginationParams;

	return (
		<Page
			pageType={pageType}
			className={classNames('search-page-variation')}
			id="search-page-variation-container"
		>
			{isSpinnerVisible && (
				<div className="search-page-variation__spinner">
					<Spinner show />
				</div>
			)}
			{shouldNoIndex && (
				<Helmet>
					<meta
						name="robots"
						content="noindex, nofollow"
					/>
				</Helmet>
			)}
			{!isSearchRoute && !searchParams.informedSearch && (
				<Helmet>
					<meta
						name="msvalidate.01"
						content="72430AA401040F456AF99A586F12B284"
					/>

					{!isNearMePage && clpPageSchema && (
						<script type="application/ld+json">
							{clpPageSchema}
						</script>
					)}
					{isNearMePage && nearMeWebpageSchema && (
						<script type="application/ld+json">
							{nearMeWebpageSchema}
						</script>
					)}
					{isNearMePage && nearMeWebSiteSchema && (
						<script type="application/ld+json">
							{nearMeWebSiteSchema}
						</script>
					)}
					{facilitySelfStorageSchemas &&
						facilitySelfStorageSchemas.map((schema, index) => (
							<script
								key={`facility-schema-${index}`}
								type="application/ld+json"
							>
								{schema}
							</script>
						))}
				</Helmet>
			)}

			<StickySearchBar
				handleSearch={handleSearch}
				searchLocation={searchLocation}
				queryParams={queryParams}
				onSearch={handleSearchParamChange}
				searchParams={searchParams}
				closeFilters={toggleFilters}
				filtersShowing={filtersShowing}
				onSearchParamChange={handleSearchParamChange}
				isLarge={isFoundationLarge(viewportWidth)}
				handleToggleFilters={handleToggleFilters}
				filterComponent={filterComponent}
				unitSizeGuideShowing={unitSizeGuideShowing}
				handleHideUnitGuide={handleHideUnitGuide}
				filtersCount={filterCount}
				clearableFilterCount={clearableFilterCount}
				primaryHeadline={primaryHeadline}
				isNearMePage={isNearMePage}
				searchType={searchType}
				nearMeSearchPath={
					isNearMePage ? getPaginationSearchPath() : undefined
				}
			/>

			<Modal
				showing={unitSizeGuideShowing}
				onClose={handleHideUnitGuide}
			>
				<ModalBody>
					<UnitSizeGuide />
				</ModalBody>
			</Modal>

			<div className="search-page-variation__content">
				<div
					className="search-container"
					id="search-container"
				>
					{!isFoundationLarge(viewportWidth) && filtersShowing && (
						<SearchFiltersTray
							handleSearch={handleSearch}
							handleToggleFilters={handleToggleFilters}
							queryParams={queryParams}
							onSearch={handleSearchParamChange}
							closeFilters={toggleFilters}
							filtersShowing={filtersShowing}
							onSearchParamChange={handleSearchParamChange}
							isLarge={isFoundationLarge(viewportWidth)}
							filtersCount={filterCount}
							clearableFilterCount={clearableFilterCount}
						>
							{filterComponent}
						</SearchFiltersTray>
					)}
					<Row>
						<Column
							large={6}
							xlarge={6}
							className={classNames('map-column', {
								'map-column--mobile-active':
									mobileView === 'map',
							})}
						>
							<div className="map-sticky-container">
								{showMap && (
									<EmbeddedSearchMap
										searchLocation={searchLocation}
										facilities={facilities}
										facilitiesPerPage={facilitiesPerPage}
										totalFacilities={totalFacilities}
										page={page}
										preselectedFacilityId={
											preselectedFacilityId
										}
										queryParams={queryParams}
										highlightedFacility={highlightedFacility}
										onSearch={handleSearchParamChange}
										onMapReady={hideSpinner}
										onMapSearchStart={showSpinner}
										onScrollToFacility={handleScrollToFacility}
										searchParams={searchParams}
										searchType={searchType}
										baseUrl={baseUrl}
									/>
								)}
							</div>
						</Column>

						{showListColumn && (
							<Column
								large={6}
								xlarge={6}
								className="list-column"
							>
								{isNearMePage &&
									match?.params?.keyword === 'storage-deals' && (
										<DealsDropdown
											className="deals-dropdown"
											options={DEALS_DROPDOWN_OPTIONS}
											value={dealsDropdownValue}
											placeholder="All Deals"
											onChange={(dealsFilter) =>
												handleSearchParamChange({
													dealsFilter,
												})
											}
										/>
									)}

								<div className="pagination-wrapper">
									<PaginationSummary
										hideIfEmpty
										page={page}
										itemsPerPage={facilitiesPerPage}
										totalItems={totalFacilities}
										prefix={searchLocation.text}
										label={`Storage Facilities`}
										isDot={true}
										isNearMePage={isNearMePage}
									/>

									{useNewFilters && (
										<SortDropDownVariation
											onChange={handleSearchParamChange}
											value={
												queryParams?.order ||
												searchParams?.order
											}
										/>
									)}
								</div>

								<FacilityList
									facilities={facilities}
									showNoResults={!loading}
									baseUrl={baseUrl}
									searchParams={searchParams}
									storageType={searchType}
									withSchema={!isSearchRoute}
									highlightedFacility={highlightedFacility}
									vipFacilityId={vipFacilityId}
									firstListingIsVipEligible={
										firstListingIsVipEligible
									}
									searchTerm={searchTerm}
									pageType={pageType}
									match={match}
								/>
								{numPages > 1 && (
									<SearchPagination
										searchParams={searchParams}
										numPages={numPages}
										selectedPage={page}
										searchPath={getPaginationSearchPath()}
									/>
								)}
							</Column>
						)}
					</Row>
				</div>

				<Column>
					<div className="seo-content-below-fold">
						<SEOContentVariation
							phone={phone}
							getTollfreePhoneNumber={
								actions.getTollfreePhoneNumber
							}
							searchParams={searchParams}
							searchLocation={searchLocation}
							searchContent={searchContent}
							faqContent={faqContent}
							faqShowing={faqShowing}
							averageUnitPriceData={
								searchContent?.averageMonthlyUnitPrice
							}
							bestPricedUnits={units}
							canonicalUrl={canonicalUrl}
							cityUrl={cityUrl}
							stateUrl={stateUrl}
							isCLP={!isSearchRoute}
							baseUrl={baseUrl}
							amenity={searchParams?.amenity}
							primaryHeadline={primaryHeadline}
							nearMeKeyword={match?.params?.keyword}
							isNearMePage={isNearMePage}
						/>
					</div>
				</Column>

				<BackToTop />
			</div>

			<SearchCTCFooter
				withinCallCenterHours={withinCallCenterHours}
				mobileView={mobileView}
				handleViewListMap={toggleMobileView}
				onToggleListMap={toggleMobileView}
				onOpenFilters={toggleFilters}
				filtersCount={filterCount}
				phone={phone}
			/>

		</Page>
	);
}

SearchPageVariation.propTypes = {
	actions: PropTypes.shape({
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func,
		getLocalPhoneNumberByCityState: PropTypes.func,
		getTollfreePhoneNumber: PropTypes.func,
	}),
	toggleFilters: PropTypes.func,
	filtersShowing: PropTypes.bool,
	handleSearchParamChange: PropTypes.func,
	handleHideUnitGuide: PropTypes.func,
	isTruckSerp: PropTypes.bool,
	queryParams: PropTypes.object,
	showHoursForFacility: PropTypes.number,
	openHoursRequest: PropTypes.func,
	closeHoursRequest: PropTypes.func,
	unitSizeGuideShowing: PropTypes.bool,
	injectedComponents: PropTypes.object,
	injectAllEvenIfNoContent: PropTypes.bool,
	showVehicleLengthTool: PropTypes.bool,
	listType: PropTypes.oneOf(['grid', 'column']),
	viewportWidth: PropTypes.number.isRequired,
	searchParams: PropTypes.shape({
		subPageType: PropTypes.string,
		informedSearch: PropTypes.bool,
	}).isRequired,
	totalFacilities: PropTypes.number,
	searchLocation: PropTypes.object,
	searchId: PropTypes.string,
	handleSearch: PropTypes.func.isRequired,
	canonicalUrl: PropTypes.string,
	staticMapUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	locationLabel: PropTypes.string,
	searchTerm: PropTypes.string,
	searchType: PropTypes.string,
	page: PropTypes.number.isRequired,
	facilitiesPerPage: PropTypes.number,
	loading: PropTypes.bool,
	localPhone: PropTypes.object,
	stateUrl: PropTypes.string,
	searchContent: PropTypes.object,
	numPages: PropTypes.number,
	phone: PropTypes.object,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	firstListingIsVipEligible: PropTypes.bool,
	vipAccount: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	vipFacilityId: PropTypes.number,
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		}),
	),
	preselectedFacilityId: PropTypes.number,
	fmfFacilitiesCount: PropTypes.number,
	promoFacilitiesCount: PropTypes.number,
	pageType: PropTypes.string,
	clpPageSchema: PropTypes.string,
	nearMeWebpageSchema: PropTypes.string,
	nearMeWebSiteSchema: PropTypes.string,
	facilitySelfStorageSchemas: PropTypes.arrayOf(PropTypes.string),
	baseUrl: PropTypes.string,
	searchPath: PropTypes.string,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			averagePrice: PropTypes.number,
			facility: PropTypes.number,
			facilityCity: PropTypes.string,
			facilityImage: PropTypes.string,
			facilityName: PropTypes.string,
			facilityState: PropTypes.string,
			inNetwork: PropTypes.bool,
			price: PropTypes.string,
			reserveUrl: PropTypes.string,
			sizeText: PropTypes.string,
			unit: PropTypes.number,
			squareFootage: PropTypes.number,
		}),
	),

	// Props used by the variation UI only
	filterComponent: PropTypes.node,
	mobileView: PropTypes.oneOf(['list', 'map']),
	toggleMobileView: PropTypes.func,
	highlightedFacility: PropTypes.number,
	isSpinnerVisible: PropTypes.bool,
	showSpinner: PropTypes.func,
	hideSpinner: PropTypes.func,
	withinCallCenterHours: PropTypes.bool,
	filterCount: PropTypes.number,
	handleToggleFilters: PropTypes.func,
	handleScrollToFacility: PropTypes.func,
	isStorageDealsPage: PropTypes.bool,
	primaryHeadline: PropTypes.string,
	prevUrl: PropTypes.string,
	nextUrl: PropTypes.string,
	match: PropTypes.object,
};

export default enhance(SearchPageVariation);
